import {
  type QueryClient,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import {
  completeOnboarding as completeOnboardingApi,
  getOnboardingAccess,
  updateOnboardingStep as updateStepApi,
} from '@/api/onboarding-api'
import { organizationKeys } from '@/hooks/organization/query-keys'
import { useAuthStore } from '@/store/auth'

/**
 * Route-gate read: may the signed-in user run this org's wizard? Only the ADMIN
 * Depanneo invited does (#2820); the API decides, and refuses the step/complete
 * writes to anyone else.
 */
export async function ensureCanRunOnboarding(
  queryClient: QueryClient,
  orgId: string
): Promise<boolean> {
  const { canRunOnboarding } = await queryClient.ensureQueryData({
    queryKey: organizationKeys.onboardingAccess(
      orgId,
      useAuthStore.getState().userId
    ),
    queryFn: () => getOnboardingAccess(orgId),
  })
  return canRunOnboarding
}

export function useUpdateOnboardingStep(orgId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (step: number) => updateStepApi(orgId, step),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: organizationKeys.detail(orgId),
      })
    },
  })
}

export function useCompleteOnboarding(orgId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => completeOnboardingApi(orgId),
    onSuccess: async () => {
      await queryClient.refetchQueries({
        queryKey: organizationKeys.detail(orgId),
      })
    },
  })
}
